import Head from "next/head";
import Link from "next/link";
import Image from "next/image";
import dynamic from "next/dynamic";
import { getPostBySlug, getAllPostSlugs, getMediaUrl } from "@/lib/payload";
import { SITE_URL } from "@/lib/constants";
import RichText from "@/components/blog/RichText";

const NewHeader = dynamic(
  () => import("@/components/NewHeader").then((mod) => mod.default),
  { ssr: true }
);

const Footer = dynamic(
  () => import("@/components/homepages/Footer").then((mod) => mod.default),
  { ssr: true }
);

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString("en-AU", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function BlogPost({ post }) {
  if (!post) {
    return (
      <>
        <NewHeader />
        <section className="pt-[140px] pb-[120px] px-6 lg:px-10 bg-cream min-h-screen">
          <div className="max-w-[800px] mx-auto text-center">
            <h1 className="font-inter text-[36px] font-extrabold tracking-[-1.5px] text-gray-cool-900 mb-4">
              Post Not Found
            </h1>
            <Link
              href="/blog"
              className="text-blue-primary-500 font-semibold hover:text-blue-primary-700 transition-colors"
            >
              Back to Blog
            </Link>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  const metaTitle = post.meta?.title || post.title;
  const metaDescription = post.meta?.description || post.excerpt;
  const heroImageUrl = getMediaUrl(post.featuredImage);

  return (
    <>
      <Head>
        <title>{metaTitle} | Ledgerowl Blog</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="article" />
        <meta
          property="og:url"
          content={`${SITE_URL}/blog/${post.slug}`}
        />
        {heroImageUrl && (
          <meta property="og:image" content={heroImageUrl} />
        )}
        <meta property="og:site_name" content="Ledgerowl" />
        <meta property="og:locale" content="en_AU" />
        <meta property="article:published_time" content={post.publishedAt} />
        <meta property="article:author" content={post.authorName} />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        {heroImageUrl && <meta name="twitter:image" content={heroImageUrl} />}
        <link
          rel="canonical"
          href={`${SITE_URL}/blog/${post.slug}`}
        />
        <link rel="icon" href="/favicon.png" />
        {/* BlogPosting structured data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BlogPosting",
              headline: post.title,
              description: post.excerpt,
              image: heroImageUrl || undefined,
              datePublished: post.publishedAt,
              dateModified: post.updatedAt,
              url: `${SITE_URL}/blog/${post.slug}`,
              mainEntityOfPage: {
                "@type": "WebPage",
                "@id": `${SITE_URL}/blog/${post.slug}`,
              },
              author: {
                "@type": "Organization",
                name: post.authorName || "Ledgerowl",
                url: SITE_URL,
              },
              publisher: {
                "@type": "Organization",
                name: "Ledgerowl",
                url: SITE_URL,
                logo: {
                  "@type": "ImageObject",
                  url: "https://ledgerowl.com.au/favicon.png",
                },
              },
              keywords: post.categories?.map((c) => c.name || c).join(", ") || "AI bookkeeping, accounting automation",
              inLanguage: "en-AU",
              isAccessibleForFree: true,
            }),
          }}
        />
        {/* Breadcrumb structured data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: SITE_URL,
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Blog",
                  item: `${SITE_URL}/blog`,
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: post.title,
                  item: `${SITE_URL}/blog/${post.slug}`,
                },
              ],
            }),
          }}
        />
      </Head>

      <NewHeader />

      <article className="pt-[140px] pb-[120px] px-6 lg:px-10 bg-cream min-h-screen">
        <div className="max-w-[800px] mx-auto">
          {/* Back link */}
          <Link
            href="/blog"
            className="inline-flex items-center gap-1.5 font-inter text-[14px] text-gray-cool-500
                       hover:text-blue-primary-500 transition-colors mb-8"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Blog
          </Link>

          {/* Categories */}
          {post.categories?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {post.categories.map((cat) => (
                <span
                  key={cat.id || cat}
                  className="text-[12px] font-semibold text-blue-primary-500 bg-blue-primary-50
                             px-3 py-1 rounded-full uppercase tracking-[0.5px]"
                >
                  {cat.name || cat}
                </span>
              ))}
            </div>
          )}

          {/* Title */}
          <h1 className="font-inter text-[32px] sm:text-[40px] lg:text-[48px] font-extrabold tracking-[-1.5px] leading-[1.15] text-gray-cool-900 mb-5">
            {post.title}
          </h1>

          {/* Author & Date */}
          <div className="flex items-center gap-4 font-inter text-[14px] text-gray-cool-500 mb-10">
            <span>By {post.authorName}</span>
            <span className="w-1 h-1 rounded-full bg-gray-cool-300" />
            <span>
              {post.publishedAt ? formatDate(post.publishedAt) : ""}
            </span>
          </div>

          {/* Hero Image */}
          {heroImageUrl && (
            <div className="relative aspect-[16/9] rounded-[20px] overflow-hidden mb-12">
              <Image
                src={heroImageUrl}
                alt={post.featuredImage?.alt || post.title}
                fill
                className="object-cover"
                priority
                sizes="(max-width: 800px) 100vw, 800px"
              />
            </div>
          )}

          {/* Content */}
          <RichText content={post.content} />

          {/* Tags */}
          {post.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-black/[0.06]">
              {post.tags.map((tag) => (
                <span
                  key={tag.id || tag}
                  className="font-inter text-[13px] text-gray-cool-500 bg-gray-cool-100
                             px-3 py-1.5 rounded-full"
                >
                  #{tag.name || tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </article>

      <Footer />
    </>
  );
}

export async function getStaticPaths() {
  try {
    const slugs = await getAllPostSlugs();
    return {
      paths: slugs.map((slug) => ({ params: { slug } })),
      fallback: "blocking",
    };
  } catch (error) {
    console.error("Error fetching post slugs:", error);
    return {
      paths: [],
      fallback: "blocking",
    };
  }
}

export async function getStaticProps({ params }) {
  try {
    const post = await getPostBySlug(params.slug);
    if (!post) {
      return { notFound: true };
    }
    return {
      props: { post },
      revalidate: 60,
    };
  } catch (error) {
    console.error("Error fetching post:", error);
    return { notFound: true };
  }
}
